<script setup>
import { useRouter, useRoute } from 'vue-router'
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import Helmet from '@/components/Helmet.vue'

const router = useRouter()
const route = useRoute()

function scrollToContact() {
  const tryScroll = (attempts = 0) => {
    const el = document.getElementById('contact')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    } else if (attempts < 10) {
      setTimeout(() => tryScroll(attempts + 1), 200)
    }
  }

  if (route.path === '/') {
    tryScroll()
  } else {
    router.push({ path: '/' }).then(() => {
      setTimeout(() => tryScroll(), 600)
    })
  }
}
</script>

<template>
  <Helmet title="SEO para emprendedores: cómo aparecer en Google sin pagar publicidad" />
  <Header />

  <section class="blog-article">
    <h2 class="title">SEO para emprendedores: cómo aparecer en Google</h2>
    <p class="date">28 de mayo de 2025</p>
    <img src="../../public/img/seo.jpg" alt="SEO para emprendedores" class="cover-image" />

    <div class="content">
      <p>
        ¿Tenés un emprendimiento y querés que te encuentren en Google sin pagar publicidad? Entonces necesitás entender cómo funciona el SEO (Search Engine Optimization), o posicionamiento orgánico.
      </p>

      <h2>¿Qué es el SEO?</h2>
      <p>
        El SEO es el conjunto de estrategias que te ayudan a que tu sitio web aparezca en los primeros resultados de Google cuando alguien busca algo relacionado a tus productos o servicios.
      </p>

      <p>
        A diferencia de la publicidad paga, el SEO no implica una inversión constante en anuncios. Requiere tiempo, constancia y contenido útil, pero a largo plazo es más rentable.
      </p>

      <h2>¿Por qué es importante para un emprendimiento?</h2>
      <ul>
        <li><strong>Visibilidad:</strong> si no estás en Google, es como si no existieras para muchos clientes.</li>
        <li><strong>Confianza:</strong> un sitio bien posicionado transmite autoridad y profesionalismo.</li>
        <li><strong>Ventas:</strong> más visitas relevantes = más posibilidades de venta.</li>
      </ul>

      <h2>¿Qué podés hacer hoy mismo?</h2>
      <ol>
        <li><strong>Tené un sitio web optimizado:</strong> rápido, adaptable a celular, con textos claros y bien estructurados.</li>
        <li><strong>Usá palabras clave:</strong> pensá qué buscaría tu cliente ideal y usalo en los títulos y contenidos.</li>
        <li><strong>Creá contenido útil:</strong> como este blog. Google premia a quienes ayudan al usuario.</li>
        <li><strong>Agregá tu negocio a Google Mi Negocio:</strong> especialmente si tenés atención presencial.</li>
        <li><strong>Conseguí enlaces de otras webs:</strong> ayuda a tu autoridad online.</li>
      </ol>

      <p>
        En resumen, el SEO no es magia ni algo exclusivo para grandes empresas. Es una herramienta poderosa que podés empezar a aprovechar desde hoy, incluso si recién estás comenzando.
      </p>

      <p>
        ¿Querés una web optimizada para SEO desde el primer día?
        <strong class="seo-strong">
          <span
            style="cursor: pointer; color: var(--first-color);"
            @click="scrollToContact"
            role="button"
            tabindex="0"
          >
            Contactanos
          </span>
        </strong> y te ayudamos.
      </p>
    </div>
  </section>

  <Footer />
</template>

<style scoped>
.blog-article {
  max-width: 800px;
  margin: 120px auto 80px;
  padding: 0 1rem;
}

.cover-image {
  width: 100%;
  height: auto;
  border-radius: 12px;
  margin-bottom: 2rem;
}

.title {
  font-size: 2.25rem;
  margin-bottom: 0.5rem;
  text-align: center;
}

.date {
  text-align: center;
  color: var(--text-color);
  font-size: 0.95rem;
  margin-bottom: 2rem;
}

.content {
  font-size: 1.1rem;
  line-height: 1.8;
}

.content h2 {
  font-size: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.content ul,
.content ol {
  margin: 1rem 0 1rem 1.5rem;
  padding-left: 1rem;
}

.content li {
  margin-bottom: 0.5rem;
}

.seo-strong:hover {
  text-decoration: underline;
}
</style>
